import axios from 'axios';

export const DOCUMENT_CONTEXT_BUYER = 2;
export const DOCUMENT_CONTEXT_PROPERTY = 3;
export const TODO_STATUS_PENDING = 0;
export const TODO_STATUS_COMPLETED = 1;

const AUDIO_UNLOCK_EVENTS = [
  'click',
  'keydown',
  'touchstart',
  'pointerdown',
];

let audioContext = null;
let pendingAudio = false;
let audioUnlockBound = false;

export const getProp = (source, names) => {
  if (!source) {
    return undefined;
  }

  for (let index = 0; index < names.length; index += 1) {
    if (Object.prototype.hasOwnProperty.call(source, names[index])) {
      return source[names[index]];
    }
  }

  return undefined;
};

export const toText = (value, fallback = '') => {
  if (value === undefined || value === null) {
    return fallback;
  }

  const text = String(value).replace(/\s+/g, ' ').trim();
  return text || fallback;
};

const parseBool = (value) => {
  if (value === true || value === false) {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return normalized === 'true' || normalized === '1';
  }

  return false;
};

const parseNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const formatNotificationDateTime = (value) => {
  return formatDateAsDayMonthYear(value);
};

export const formatDateAsDayMonthYear = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return `${day}/${month}/${year}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatNotificationDate = (value) => {
  return formatDateAsDayMonthYear(value);
};

export const getNotificationStatusText = (status, statusName) => {
  const explicitName = toText(statusName);

  if (explicitName) {
    return explicitName;
  }

  switch (parseNumber(status, -1)) {
    case TODO_STATUS_PENDING:
      return 'Pending';
    case TODO_STATUS_COMPLETED:
      return 'Completed';
    case 5:
      return 'Expired';
    case 9:
      return 'Cancelled';
    default:
      return toText(status, '-');
  }
};

export const getExpectedDateText = (raw) => {
  const formatted = toText(
    getProp(raw, [
      'expectedCompletionDateFormatted',
      'ExpectedCompletionDateFormatted',
    ]),
  );

  const formattedDate = formatDateAsDayMonthYear(formatted);

  if (formattedDate) {
    return formattedDate;
  }

  return formatNotificationDate(
    getProp(raw, ['expectedCompletionDate', 'ExpectedCompletionDate']),
  );
};

const unwrapPayload = (payload) => {
  if (!payload || Array.isArray(payload)) {
    return payload;
  }

  return (
    getProp(payload, ['data', 'Data', 'object', 'Object']) ||
    payload
  );
};

export const unwrapNotificationList = (payload) => {
  const unwrapped = unwrapPayload(payload);
  const list = getProp(unwrapped, ['objectList', 'ObjectList']) || unwrapped;

  return Array.isArray(list) ? list : [];
};

export const normalizeNotification = (raw) => {
  const id = parseNumber(getProp(raw, ['id', 'Id']));
  const taskTypeName = toText(
    getProp(raw, ['taskTypeName', 'TaskTypeName']),
    'Notification',
  );
  const reminderText = toText(
    getProp(raw, ['reminderText', 'ReminderText']),
  );
  const recordRef = toText(getProp(raw, ['recordRef', 'RecordRef']));
  const expectedCompletionDate = getProp(raw, [
    'expectedCompletionDate',
    'ExpectedCompletionDate',
  ]);
  const dateTimeRaised = getProp(raw, [
    'dateTimeRaised',
    'DateTimeRaised',
  ]);
  const status = parseNumber(getProp(raw, ['status', 'Status']));
  const statusText = getNotificationStatusText(
    status,
    getProp(raw, ['toDoStatusName', 'ToDoStatusName']),
  );
  const expectedDate = getExpectedDateText(raw);
  const raisedDate = formatNotificationDateTime(dateTimeRaised);
  const staffMemberName = toText(
    getProp(raw, ['staffMemberName', 'StaffMemberName']),
  );
  const meta = expectedDate || raisedDate || recordRef || staffMemberName;
  const raisedAtMs = dateTimeRaised
    ? new Date(dateTimeRaised).getTime()
    : 0;

  return {
    id,
    taskTypeId: parseNumber(
      getProp(raw, ['taskTypeId', 'TaskTypeId']),
    ),
    title: taskTypeName,
    taskTypeName,
    message: reminderText || recordRef || 'You have a new notification.',
    reminderText,
    recordRef,
    documentContextId: parseNumber(
      getProp(raw, ['documentContextId', 'DocumentContextId']),
    ),
    recordId: parseNumber(getProp(raw, ['recordId', 'RecordId'])),
    expectedCompletionDate,
    expectedDate,
    dateTimeRaised,
    raisedDate,
    raisedAtMs: Number.isNaN(raisedAtMs) ? 0 : raisedAtMs,
    status,
    statusText,
    isRead: parseBool(
      getProp(raw, ['isNotificationRead', 'IsNotificationRead']),
    ),
    staffMemberName,
    meta,
    raw: raw || {},
  };
};

export const normalizeNotifications = (payload) =>
  unwrapNotificationList(payload)
    .map(normalizeNotification)
    .filter((notification) => notification.id > 0);

export const normalizeNotificationSummary = (payload) => {
  const unwrapped = unwrapPayload(payload) || {};
  const unreadCount = parseNumber(
    getProp(unwrapped, ['unreadCount', 'UnreadCount']),
  );
  const latestUnreadNotifications = normalizeNotifications(
    getProp(unwrapped, [
      'latestUnreadNotifications',
      'LatestUnreadNotifications',
    ]) || [],
  );

  return {
    unreadCount,
    latestUnreadNotifications,
  };
};

export const fetchNotificationSummary = async () => {
  const response = await axios.get('/ToDoList/GetNotificationSummary');
  return normalizeNotificationSummary(response?.data);
};

export const fetchNotifications = async (unreadOnly = true) => {
  const response = await axios.get('/ToDoList/GetNotifications', {
    params: { unreadOnly },
  });
  return normalizeNotifications(response?.data);
};

export const markNotificationRead = async (id) => {
  await axios.post(`/ToDoList/MarkNotificationRead/${id}`);
};

export const markNormalizedNotificationRead = (notification) => ({
  ...notification,
  isRead: true,
  status: TODO_STATUS_COMPLETED,
  statusText: 'Completed',
  raw: {
    ...(notification?.raw || {}),
    isNotificationRead: true,
    IsNotificationRead: true,
    status: TODO_STATUS_COMPLETED,
    Status: TODO_STATUS_COMPLETED,
  },
});

export const getNotificationOpenTarget = (notification) => {
  if (!notification?.recordId) {
    return null;
  }

  if (notification.documentContextId === DOCUMENT_CONTEXT_PROPERTY) {
    return {
      type: 'property',
      label: 'Open Property',
    };
  }

  if (notification.documentContextId === DOCUMENT_CONTEXT_BUYER) {
    return {
      type: 'buyer',
      label: 'Open Buyer',
    };
  }

  return null;
};

const getAudioContext = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const AudioContextType = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextType) {
    return null;
  }

  if (!audioContext) {
    audioContext = new AudioContextType();
  }

  return audioContext;
};

const playTone = (context, frequency, start, duration) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.08, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
};

const playChimeNow = (context) => {
  const now = context.currentTime;

  playTone(context, 880, now, 0.16);
  playTone(context, 1175, now + 0.17, 0.2);
};

const removeAudioUnlockHandlers = (handler) => {
  AUDIO_UNLOCK_EVENTS.forEach((eventName) => {
    document.removeEventListener(eventName, handler, true);
  });
  audioUnlockBound = false;
};

const bindAudioUnlock = () => {
  if (audioUnlockBound || typeof document === 'undefined') {
    return;
  }

  audioUnlockBound = true;

  const handler = () => {
    const context = getAudioContext();
    const resume =
      context && context.state === 'suspended' && context.resume
        ? context.resume()
        : Promise.resolve();

    resume
      .then(() => {
        removeAudioUnlockHandlers(handler);

        if (pendingAudio && context && context.state !== 'suspended') {
          pendingAudio = false;
          playChimeNow(context);
        }
      })
      .catch(() => {
        pendingAudio = true;
      });
  };

  AUDIO_UNLOCK_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, handler, true);
  });
};

export const playNotificationChime = () => {
  try {
    const context = getAudioContext();

    if (!context) {
      return;
    }

    if (context.state === 'suspended') {
      pendingAudio = true;
      bindAudioUnlock();

      if (context.resume) {
        context
          .resume()
          .then(() => {
            if (pendingAudio && context.state !== 'suspended') {
              pendingAudio = false;
              playChimeNow(context);
            }
          })
          .catch(() => {
            pendingAudio = true;
          });
      }

      return;
    }

    playChimeNow(context);
  } catch (error) {
    pendingAudio = true;
    bindAudioUnlock();
  }
};
